import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadError } from '../../components/LoadError';
import { ErrorSummary } from '../../components/FormFeedback';
import { PageFrame } from '../../components/PageFrame';
import { ContentUnavailableState } from './ContentUnavailableState';
import { SafeExitLink } from './SafeExitLink';
import type {
  createLearnerSessionClient,
  CurrentPractice,
} from '../../platform/learner/learner-session-client';

// Matches StudentStart's own precedent: one fixed, learner-safe message,
// never the raw error text or a server-specific reason.
const GENERIC_START_ERROR =
  "That didn't work — check your connection and try again.";

type Props = {
  client: Pick<
    ReturnType<typeof createLearnerSessionClient>,
    'getCurrentPractice' | 'sendAction'
  >;
};

// "5 questions", "5 questions · up to 10 minutes", or "10 minutes"
// (test run 2026-10-02: it said "tasks", and never mentioned a timer).
function sessionLengthText(practice: {
  itemsTotal: number;
  stoppingRuleType: 'item_count' | 'time_limit';
  timeLimitSeconds?: number | null;
}) {
  const minutes =
    typeof practice.timeLimitSeconds === 'number'
      ? Math.round(practice.timeLimitSeconds / 60)
      : null;
  const minutesText =
    minutes === null
      ? null
      : `${String(minutes)} minute${minutes === 1 ? '' : 's'}`;
  if (practice.stoppingRuleType === 'time_limit')
    return minutesText ?? 'Practice';
  const questions = `${String(practice.itemsTotal)} question${practice.itemsTotal === 1 ? '' : 's'}`;
  return minutesText ? `${questions} · up to ${minutesText}` : questions;
}

export function SessionIntroduction({ client }: Props) {
  const navigate = useNavigate();
  const [practice, setPractice] = useState<CurrentPractice | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setPractice(null);
    setLoadError(false);
    client
      .getCurrentPractice()
      .then((result) => {
        if (active) setPractice(result);
      })
      .catch(() => {
        if (active) setLoadError(true);
      });
    return () => {
      active = false;
    };
  }, [client, reloadKey]);

  async function handleStart() {
    setStarting(true);
    setStartError(null);
    try {
      await client.sendAction('start', {}, crypto.randomUUID());
      void navigate('/learn/focus', { replace: true });
    } catch {
      setStarting(false);
      setStartError(GENERIC_START_ERROR);
    }
  }

  if (loadError) {
    return (
      <PageFrame title="Your activity" audience="learner">
        <div className="mt-4">
          <LoadError
            message="Your activity could not be loaded."
            onRetry={() => {
              setReloadKey((k) => k + 1);
            }}
          />
        </div>
        <SafeExitLink />
      </PageFrame>
    );
  }

  if (practice === null) {
    return (
      <PageFrame title="Your activity" audience="learner">
        <p role="status" className="mt-4 text-muted">
          Loading your activity…
        </p>
      </PageFrame>
    );
  }

  if (practice.contentEligibilityException === 'CONTENT_INELIGIBLE') {
    // Deliberately generic: resolve_current_practice can flag this because
    // the assigned item is a teacher-facilitation-note item, but the learner
    // must never see or infer that specific reason — just a calm, neutral
    // "not available" state.
    return <ContentUnavailableState />;
  }

  return (
    <PageFrame title="Ready to start?" audience="learner">
      {/* No greeting here: the student was just greeted by name on the
          start screen ("Hi Mango!"), so a second "Hi there!" read oddly
          (owner approved 31 Oct). */}
      {/* What this activity is, grouped in one calm panel (it used to be
          four look-alike lines). */}
      <div className="mt-3 rounded-xl border border-forest-600/15 bg-sage px-4 py-3">
        <p className="text-lg font-semibold text-ink">{practice.purpose}</p>
        <p className="mt-1 text-sm font-semibold text-forest-800">
          {sessionLengthText(practice)}
        </p>
      </div>
      <p className="mt-4 text-[15px] text-muted">
        Grab a pencil and some paper for rough working, if you'd like.
      </p>

      {startError && (
        <div className="mt-6">
          <ErrorSummary errors={[startError]} />
        </div>
      )}

      {/*
        One clear action before the activity starts (design audit 2,
        2026-10-04). Help and Pause used to sit here as disabled
        placeholders: children read them as broken buttons, and S-SCR-002
        allows "Start/Help/Pause or safe-exit actions", so Start plus the
        safe-exit link below meets it. Help and Pause appear on the
        practice screen, where they actually work.
      */}
      <div className="mt-8">
        <button
          type="button"
          disabled={starting}
          className="dm-btn-primary w-full"
          onClick={() => {
            void handleStart();
          }}
        >
          Start
        </button>
      </div>

      <SafeExitLink />
    </PageFrame>
  );
}
